import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import '../universal.css';
import UniversalToast from './UniversalToast';
import { convertMentionsToLinks, handleMentionClick } from '../utils/mentionUtils';
import {
  Heart,
  MessageCircle,
  Share,
  MoreHorizontal,
  Edit,
  Trash2,
  BookOpen,
  Mic,
  Film,
  UserRound,
  Link as LinkIcon,
  FileText,
  ArrowUp
} from 'lucide-react';
import api from '../services/api';
import { Post } from '../types';
import './PostCard.css';

// HTML sanitization function using DOMPurify to allow safe tags (<br>, <strong>, <em>) and convert mentions
const sanitizeHtml = (input: string): string => {
  if (!input) return '';

  // First convert @ mentions to clickable links
  const withMentions = convertMentionsToLinks(input);

  // Use DOMPurify to sanitize HTML, allowing only safe tags
  return DOMPurify.sanitize(withMentions, {
    ALLOWED_TAGS: ['br', 'strong', 'em', 'a', 'div'],
    ALLOWED_ATTR: ['href', 'data-username']
  });
};

// Decode common HTML entities (for titles/authors coming from external sources)
const decodeHtmlEntities = (input: string): string => {
  if (!input) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = input;
  return textarea.value;
};

// Helper function to sanitize HTML using DOMPurify (allow only <strong>, <em>, <br>)
const sanitizeCommentHtml = (html: string): string => {
  // Use DOMPurify to sanitize HTML, allowing only safe tags
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['br', 'strong', 'em', 'div'],
    ALLOWED_ATTR: []
  });
};

interface PostCardProps {
  post: Post;
  onLikeToggle: (postId: string, liked: boolean, likes_count: number) => void;
  onDelete: (postId: string) => void;
  onEdit: (postId: string, newContent: string) => void;
  feedType?: 'foryou' | 'following';
  feedPostsListRef?: React.RefObject<HTMLDivElement | null>;
  onCommentAdded?: () => void;
  isClickable?: boolean;
}

const PostCard: React.FC<PostCardProps> = ({ post, onLikeToggle, onDelete, onEdit, feedType, feedPostsListRef, onCommentAdded, isClickable = true }) => {
  const navigate = useNavigate();

  // Format large numbers with K/M suffixes (truncate, don't round)
  const formatLikeCount = (count: number): string => {
    if (count >= 1000000) {
      const millions = Math.floor(count / 100000) / 10; // Truncate to 1 decimal
      return millions.toString().replace(/\.0$/, '') + 'M';
    } else if (count >= 10000) {
      const thousands = Math.floor(count / 100) / 10; // Truncate to 1 decimal
      return thousands.toString().replace(/\.0$/, '') + 'K';
    }
    return count.toString();
  };
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [editContent, setEditContent] = useState(post.post_content || '');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showLoadingToast, setShowLoadingToast] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comments_count ?? 0);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  const commentTextareaRef = useRef<HTMLDivElement>(null);

  // Sync comment count with prop changes (e.g., when comments are deleted in PostDetail)
  useEffect(() => {
    setCommentCount(post.comments_count ?? 0);
  }, [post.comments_count]);

  // Lock/unlock body scroll when any modal is opened/closed
  useEffect(() => {
    const anyModalOpen = showDeleteModal || showEditModal || showShareModal;

    if (anyModalOpen) {
      // Disable scrolling
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scrolling is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDeleteModal, showEditModal, showShareModal]);

  // Get current user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isOwnPost = user && post.username === user.username;

  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setShowOptionsMenu(false);
      }
    };

    if (showOptionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsMenu]);

  // Auto-resize textarea when edit modal opens
  useEffect(() => {
    if (showEditModal) {
      const textarea = document.querySelector('.postcard-edit-textarea') as HTMLTextAreaElement;
      if (textarea) {
        // Set initial height based on content
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    }
  }, [showEditModal]);

  const handleLike = async () => {
    // Optimistic update - calculate new state immediately
    const currentLiked = post.is_liked ?? false;
    const currentLikesCount = post.likes_count;
    const newLikedState = !currentLiked;
    const newLikesCount = newLikedState ? currentLikesCount + 1 : currentLikesCount - 1;

    // Trigger heart pump animation immediately
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300); // Match animation duration (0.3s)

    // Update UI immediately (optimistic update)
    onLikeToggle(post.id, newLikedState, Math.max(0, newLikesCount)); // Ensure likes don't go below 0

    try {
      // Make API call in background
      const response = await api.post(`/posts/${post.id}/like`);
      const { liked, likes_count } = response.data;

      // If backend response differs from optimistic update, correct it
      if (liked !== newLikedState || likes_count !== newLikesCount) {
        onLikeToggle(post.id, liked, likes_count);
      }
    } catch (error) {
      // Revert optimistic update on error
      onLikeToggle(post.id, currentLiked, currentLikesCount);
    }
  };

  const handleDeleteClick = () => {
    setShowOptionsMenu(false);
    setShowDeleteModal(true);
    document.body.classList.add('modal-open');
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);
    document.body.classList.remove('modal-open');
    setIsDeleting(true);
    showLoadingToastMessage('Deleting post...');

    try {
      await api.delete(`/posts/${post.id}`);
      setShowLoadingToast(false);
      showSuccessToastMessage('Post deleted successfully!');

      // Delay the onDelete call to allow success toast to show
      setTimeout(() => {
        onDelete(post.id);
      }, 1500);
    } catch (error) {
      setShowLoadingToast(false);
      showErrorToastMessage('Failed to delete post. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    document.body.classList.remove('modal-open');
  };

  const handleEditClick = () => {
    setShowOptionsMenu(false);
    setEditContent(post.post_content || '');
    setShowEditModal(true);
    document.body.classList.add('modal-open');
  };

  const handleEditableInput = (e: React.FormEvent<HTMLDivElement>) => {
    const div = e.currentTarget;
    const text = div.innerText;
    const html = div.innerHTML;

    // Check if content is empty
    if (!text.trim()) {
      div.innerHTML = '';
      setEditContent('');
      div.classList.add('empty');
      div.style.height = 'auto';
      div.style.height = div.scrollHeight + 'px';
      return;
    }

    // Enforce max length
    if (text.length <= 1000) {
      setEditContent(html);
    } else {
      // Truncate content if exceeds limit
      const truncated = text.substring(0, 1000);
      div.innerText = truncated;
      setEditContent(div.innerHTML);

      // Move cursor to end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(div);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    // Update placeholder visibility
    div.classList.remove('empty');

    // Auto-resize
    div.style.height = 'auto';
    div.style.height = div.scrollHeight + 'px';
  };

  const editTextareaRef = useRef<HTMLDivElement>(null);

  // Initialize empty class for placeholder and set initial content
  useEffect(() => {
    if (editTextareaRef.current && showEditModal) {
      // Set initial content when modal opens
      editTextareaRef.current.innerHTML = editContent;

      // Set empty class
      if (editContent.trim() === '') {
        editTextareaRef.current.classList.add('empty');
      } else {
        editTextareaRef.current.classList.remove('empty');
      }

      // Auto-resize to fit content
      editTextareaRef.current.style.height = 'auto';
      editTextareaRef.current.style.height = editTextareaRef.current.scrollHeight + 'px';
    }
  }, [showEditModal]); // Only run when modal opens

  // Update empty class when content changes
  useEffect(() => {
    if (editTextareaRef.current) {
      if (editContent.trim() === '') {
        editTextareaRef.current.classList.add('empty');
      } else {
        editTextareaRef.current.classList.remove('empty');
      }
    }
  }, [editContent]);

  // Update empty class for comment textarea
  useEffect(() => {
    if (commentTextareaRef.current) {
      if (commentText.trim() === '') {
        commentTextareaRef.current.classList.add('empty');
      } else {
        commentTextareaRef.current.classList.remove('empty');
      }
    }
  }, [commentText]);

  const confirmEdit = async () => {
    const textContent = editTextareaRef.current?.innerText || '';

    // Check if content is empty
    if (!textContent.trim()) {
      showErrorToastMessage('Post content cannot be empty');
      return;
    }

    if (textContent.length > 1000) {
      showErrorToastMessage('Post must be 1000 characters or less');
      return;
    }

    setShowEditModal(false);
    document.body.classList.remove('modal-open');
    setIsEditing(true);
    showLoadingToastMessage('Saving changes...');

    try {
      // Linkify mentions and sanitize while preserving anchors
      const linkified = (editTextareaRef.current?.innerHTML || '')
        .replace(/(^|\s)@([A-Za-z0-9_.]+)/g, (full, lead, uname) => {
          const lower = (uname || '').toLowerCase();
          return `${lead}<a data-mention="${lower}">@${uname}</a>`;
        });
      const sanitized = DOMPurify.sanitize(linkified, {
        ALLOWED_TAGS: ['br', 'strong', 'em', 'a', 'div'],
        ALLOWED_ATTR: ['data-mention']
      });
      const response = await api.put(`/posts/${post.id}`, { post_content: sanitized });
      setShowLoadingToast(false);
      showSuccessToastMessage('Post updated successfully!');

      // Delay the onEdit call to allow success toast to show
      setTimeout(() => {
        onEdit(post.id, editContent);
      }, 1500);
    } catch (error) {
      setShowLoadingToast(false);
      showErrorToastMessage('Failed to edit post. Please try again.');
    } finally {
      setIsEditing(false);
    }
  };

  const cancelEdit = () => {
    setEditContent(post.post_content || '');
    setShowEditModal(false);
    document.body.classList.remove('modal-open');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMilliseconds = now.getTime() - date.getTime();

    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInMinutes < 1) {
      return 'just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d`;
    } else if (diffInWeeks < 52) {
      return `${diffInWeeks}w`;
    } else {
      return `${diffInYears}y`;
    }
  };


  const handlePostClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or links
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('.post-actions')) {
      return;
    }

    // If the user has selected text, don't navigate
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      return;
    }

    // Save current scroll position and location before navigating
    if (feedPostsListRef?.current && feedType) {
      const scrollPosition = feedPostsListRef.current.scrollTop;
      sessionStorage.setItem(`feedScrollPosition_${feedType}`, scrollPosition.toString());
    }

    // Save the current location
    const currentPath = window.location.pathname;
    sessionStorage.setItem('previousLocation', currentPath);

    navigate(`/post/${post.id}`);
  };

  const handleShareClick = () => {
    setShowShareModal(true);
    document.body.classList.add('modal-open');
  };

  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCommentInput(!showCommentInput);

    // Focus the comment textarea after it expands
    if (!showCommentInput) {
      setTimeout(() => {
        commentTextareaRef.current?.focus();
      }, 100);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const textContent = commentTextareaRef.current?.innerText || '';

    if (!textContent.trim()) {
      showErrorToastMessage('Comment cannot be empty');
      return;
    }

    setIsSubmittingComment(true);
    showLoadingToastMessage('Posting comment...');

    try {
      const sanitizedContent = sanitizeCommentHtml(commentText).trim();

      const response = await api.post(`/posts/${post.id}/comments`, {
        post_id: post.id,
        content: sanitizedContent
      });

      setShowLoadingToast(false);
      showSuccessToastMessage('Comment posted successfully!');

      // Clear the comment input
      setCommentText('');
      if (commentTextareaRef.current) {
        commentTextareaRef.current.innerHTML = '';
      }

      // Increment comment count
      setCommentCount(prevCount => prevCount + 1);

      // Call the callback if provided (for PostDetail to refresh comments)
      if (onCommentAdded) {
        onCommentAdded();
      }

      // Close the comment input after successful submission
      setShowCommentInput(false);

    } catch (error) {
      setShowLoadingToast(false);
      showErrorToastMessage('Failed to post comment. Please try again.');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCommentInput = (e: React.FormEvent<HTMLDivElement>) => {
    const div = e.currentTarget;
    const text = div.innerText;

    // If text is empty, clear all HTML including <br> tags
    if (text.trim() === '') {
      div.innerHTML = '';
      setCommentText('');
      div.classList.add('empty');
      div.style.height = 'auto';
      div.style.height = div.scrollHeight + 'px';
      return;
    }

    // Enforce max length
    if (text.length <= 1000) {
      // Don't sanitize during input - let the user see the content as-is
      // Sanitization will happen when they submit the comment
      setCommentText(div.innerHTML);
    } else {
      // Truncate content if exceeds limit
      const truncated = text.substring(0, 1000);
      div.innerText = truncated;
      setCommentText(div.innerHTML);

      // Move cursor to end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(div);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    // Update placeholder visibility
    div.classList.remove('empty');

    // Auto-resize
    div.style.height = 'auto';
    div.style.height = div.scrollHeight + 'px';
  };

  const handleCommentPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    // Get HTML content from clipboard
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    // Function to sanitize HTML using DOMPurify, keeping only bold and italic
    const sanitizeHtml = (htmlString: string): string => {
      return DOMPurify.sanitize(htmlString, {
        ALLOWED_TAGS: ['strong', 'em', 'br'],
        ALLOWED_ATTR: []
      });
    };

    // Use HTML if available, otherwise use plain text
    let contentToPaste;
    if (html) {
      // Convert <div> tags to <br> tags before sanitization
      let convertedHtml = html
        .replace(/<div[^>]*><br[^>]*><\/div>/g, '<br>') // Empty div with br -> single br
        .replace(/<div[^>]*><\/div>/g, '<br>') // Empty div -> single br
        .replace(/<div[^>]*>/g, '<br>') // Other divs -> br
        .replace(/<\/div>/g, ''); // Remove closing divs
      contentToPaste = sanitizeHtml(convertedHtml);
    } else {
      // Convert plain text newlines to <br> tags
      contentToPaste = text.replace(/\n/g, '<br>');
    }

    // Normalize whitespace: replace multiple consecutive newlines with max 2
    contentToPaste = contentToPaste.replace(/\n{3,}/g, '\n\n');

    // Insert at cursor position
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();

      // Check if content has HTML tags
      const hasHtmlTags = /<\/?(?:strong|em|br)>/i.test(contentToPaste);

      if (hasHtmlTags) {
        // Insert as HTML to preserve formatting
        const div = document.createElement('div');
        div.innerHTML = contentToPaste;

        const fragment = document.createDocumentFragment();
        let node;
        while ((node = div.firstChild)) {
          fragment.appendChild(node);
        }
        range.insertNode(fragment);
      } else {
        // Insert as text node to preserve newlines
        const textNode = document.createTextNode(contentToPaste);
        range.insertNode(textNode);
      }

      // Move cursor to end of pasted content
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);

      // Update state and check length
      const currentDiv = e.currentTarget;
      const currentText = currentDiv.innerText;
      if (currentText.length > 1000) {
        currentDiv.innerText = currentText.substring(0, 1000);
      }
      setCommentText(sanitizeCommentHtml(currentDiv.innerHTML));

      // Auto-resize after paste
      currentDiv.style.height = 'auto';
      currentDiv.style.height = currentDiv.scrollHeight + 'px';
    }
  };

  const handleShareClose = () => {
    setShowShareModal(false);
    setCopySuccess(false);
    document.body.classList.remove('modal-open');
  };

  const showErrorToastMessage = (message: string) => {
    setErrorMessage(message);
    setShowErrorToast(true);
    setTimeout(() => setShowErrorToast(false), 4000);
  };

  const showSuccessToastMessage = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const showLoadingToastMessage = (message: string) => {
    setLoadingMessage(message);
    setShowLoadingToast(true);
  };

  const copyToClipboard = async () => {
    const url = `${window.location.origin}/post/${post.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setShowCopyToast(true);
      setTimeout(() => setCopySuccess(false), 2000);
      setTimeout(() => setShowCopyToast(false), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setShowCopyToast(true);
        setTimeout(() => setCopySuccess(false), 2000);
        setTimeout(() => setShowCopyToast(false), 3000);
      } catch (fallbackErr) {
        // Fallback copy failed
      }
      document.body.removeChild(textArea);
    }
  };

  const shareToTwitter = () => {
    const url = `${window.location.origin}/post/${post.id}`;
    const text = `Check out this ${post.content_type}: "${decodeHtmlEntities(post.content_title)}" by ${decodeHtmlEntities(post.content_author)}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, '_blank');
  };

  const shareToFacebook = () => {
    const url = `${window.location.origin}/post/${post.id}`;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, '_blank');
  };

  const shareToLinkedIn = () => {
    const url = `${window.location.origin}/post/${post.id}`;
    const title = `${decodeHtmlEntities(post.content_title)} by ${decodeHtmlEntities(post.content_author)}`;
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
    window.open(linkedinUrl, '_blank');
  };

  const shareToWhatsApp = () => {
    const url = `${window.location.origin}/post/${post.id}`;
    const text = `Check out @${post.username}'s post on "${decodeHtmlEntities(post.content_title)}" by ${decodeHtmlEntities(post.content_author)}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleContentRedirect = (post: Post) => {

    if (post.content_type === 'book') {
      // Books always go to Amazon search
      const searchQuery = `${decodeHtmlEntities(post.content_title)} ${decodeHtmlEntities(post.content_author)}`;
      const amazonUrl = `https://www.amazon.com/s?k=${encodeURIComponent(searchQuery)}`;
      window.open(amazonUrl, '_blank');
    } else if (post.content_type === 'podcast') {
      // If we have a direct video URL, use it; otherwise fall back to YouTube search
      if (post.content_url) {
        window.open(post.content_url, '_blank');
      } else {
        // Fallback to YouTube search if no direct URL
        const searchQuery = `${decodeHtmlEntities(post.content_title)} ${decodeHtmlEntities(post.content_author)}`;
        const youtubeUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
        window.open(youtubeUrl, '_blank');
      }
    } else if (post.content_type === 'movie') {
      // Open IMDb page if we have the URL, otherwise search
      if (post.content_url) {
        window.open(post.content_url, '_blank');
      } else {
        const searchQuery = decodeHtmlEntities(post.content_title);
        const imdbUrl = `https://www.imdb.com/find?q=${encodeURIComponent(searchQuery)}`;
        window.open(imdbUrl, '_blank');
      }
    } else if (post.content_type === 'article') {
      // Articles open the URL directly
      if (post.content_url) {
        window.open(post.content_url, '_blank');
      }
    } else if (post.content_type === 'pdf') {
      // PDFs open the file URL directly
      if (post.content_url) {
        window.open(post.content_url, '_blank');
      }
    }
  };

  return (
    <>
      <div
        className={`postcard-container ${isClickable ? 'postcard-clickable' : ''}`}
        onClick={isClickable ? handlePostClick : undefined}
      >
        <div className="postcard-header">
          <div className="postcard-user-info">
            <Link to={`/profile/${post.username}`} className="postcard-user-avatar-link">
              <div className="postcard-user-avatar">
                {post.profile_picture_url ? (
                  <img
                    src={post.profile_picture_url}
                    alt={`${post.username}'s profile`}
                    className="postcard-user-avatar-image"
                    loading="lazy"
                  />
                ) : (
                  <UserRound className="postcard-user-avatar-icon" />
                )}
              </div>
            </Link>
            <div className="postcard-user-details">
              <div className="postcard-user-name-row">
                <Link to={`/profile/${post.username}`} className="postcard-username-link">
                  <span className="postcard-username">@{post.username}</span>
                </Link>
              </div>
              <div className="postcard-time">
                {formatDate(post.created_at)}{formatDate(post.created_at) !== 'just now' && <p>ago</p>}
              </div>
            </div>
          </div>

          {isOwnPost && (
            <div className="postcard-actions-menu" ref={optionsMenuRef}>
              <button
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                className="postcard-action-button postcard-options-button"
                title="Post options"
              >
                <MoreHorizontal className="postcard-action-icon" />
              </button>

              {showOptionsMenu && (
                <div className="postcard-options-dropdown">
                  <button
                    onClick={handleEditClick}
                    className="postcard-dropdown-item"
                  >
                    Edit
                    <Edit className="postcard-dropdown-icon" />

                  </button>
                  <button
                    onClick={handleDeleteClick}
                    className="postcard-dropdown-item postcard-delete-item"
                  >
                    Delete
                    <Trash2 className="postcard-dropdown-icon" />

                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Book/Content Info */}
        <div className="postcard-content-info">
          <div className="postcard-content-header">
            {post.content_type !== 'pdf' && (
              <>
                {post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt={post.content_title}
                    loading="lazy"
                    className={`postcard-cover-image ${post.content_type === 'podcast' ? 'postcard-cover-image-podcast' : post.content_type === 'article' ? 'postcard-cover-image-article' : post.content_type === 'movie' ? 'postcard-cover-image-movie' : 'postcard-cover-image-book'}`}
                    style={post.content_type === 'article' && post.image_width && post.image_height ? {
                      aspectRatio: `${post.image_width} / ${post.image_height}`
                    } : post.content_type === 'podcast' ? {
                      aspectRatio: '16 / 9'
                    } : undefined}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="postcard-cover-placeholder"
                  style={{
                    display: post.cover_image_url ? 'none' : 'flex'
                  }}
                >
                  {post.content_type === 'movie' ? (
                    <Film className="postcard-cover-placeholder-icon" />
                  ) : post.content_type === 'podcast' ? (
                    <Mic className="postcard-cover-placeholder-icon" />
                  ) : post.content_type === 'article' ? (
                    <LinkIcon className="postcard-cover-placeholder-icon" />
                  ) : (
                    <BookOpen className="postcard-cover-placeholder-icon" />
                  )}
                </div>
              </>
            )}
            <div className="postcard-content-details">
              <div className="postcard-title-with-type">
                <h4 className="postcard-content-title">
                  {decodeHtmlEntities(post.content_title)}
                </h4>
              </div>
              {(post.content_type === 'book' || post.content_type === 'podcast' || post.content_type === 'movie' || post.content_type === 'article') && post.content_author && (
                <p className="postcard-content-author">
                  by {decodeHtmlEntities(post.content_author)}
                </p>
              )}
              {post.content_type === 'movie' && post.imdb_rating && (
                <div className="postcard-imdb-rating">
                  <span className="postcard-imdb-label">IMDb Rating:</span>
                  <span className="postcard-imdb-score">{post.imdb_rating}/10</span>
                  {post.imdb_votes && (
                    <span className="postcard-imdb-votes">({post.imdb_votes} votes)</span>
                  )}
                </div>
              )}
              <button
                onClick={() => handleContentRedirect(post)}
                className="postcard-content-redirect-button"
                title={
                  post.content_type === 'book' ? 'Buy on Amazon' :
                    post.content_type === 'podcast' ? (post.content_url ? 'Watch Now' : 'Find on Spotify') :
                      post.content_type === 'movie' ? 'View' :
                        post.content_type === 'article' ? 'Read Article' :
                          post.content_type === 'pdf' ? 'View' : 'View'
                }
              >
                {post.content_type === 'book' ? (
                  <>
                    Read Now
                    <BookOpen className="postcard-content-type-icon" />
                  </>
                ) : post.content_type === 'podcast' ? (
                  <>
                    {post.content_url ? 'Watch Now' : 'Find on Spotify'}
                    <Mic className="postcard-content-type-icon" />
                  </>
                ) : post.content_type === 'movie' ? (
                  <>
                    View
                    <Film className="postcard-content-type-icon" />
                  </>
                ) : post.content_type === 'article' ? (
                  <>
                    Read Article
                    <FileText className="postcard-content-type-icon" />
                  </>
                ) : post.content_type === 'pdf' ? (
                  <>
                    Open
                    <FileText className="postcard-content-type-icon" />
                  </>
                ) : (
                  <>
                    View
                    <BookOpen className="postcard-content-type-icon" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* User's thoughts */}
        {post.post_content && (
          <div className="postcard-user-content" onClick={(e) => handleMentionClick(e, navigate)}>
            <div
              className="postcard-content-display"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.post_content) }}
            ></div>
          </div>
        )}

        <div className="postcard-actions">
          <div className="postcard-left-actions">
            <button
              onClick={handleLike}
              className={`postcard-like-button ${post.is_liked ? 'postcard-liked' : ''} ${isAnimating ? 'postcard-animating' : ''}`}
            >
              <div className="postcard-like-icon-container">
                {post.is_liked ? (
                  <Heart className={`postcard-heart-icon postcard-liked ${isAnimating ? 'postcard-animating' : ''}`} fill="currentColor" />
                ) : (
                  <Heart className={`postcard-heart-icon ${isAnimating ? 'postcard-animating' : ''}`} />
                )}
              </div>
              <span className="postcard-like-count">{formatLikeCount(post.likes_count)}</span>
            </button>

            <button
              onClick={handleCommentClick}
              className="postcard-comment-button"
              title="Comment on post"
            >
              <MessageCircle className="postcard-comment-icon" />
              <span className="postcard-comment-count">{formatLikeCount(commentCount)}</span>
            </button>

          </div>

          <button
            onClick={handleShareClick}
            className="postcard-share-button"
            title="Share post"
          >
            <Share className="postcard-share-icon" />
          </button>
        </div>

        {/* Inline Comment Input */}
        {showCommentInput && (
          <div className="postcard-comment-section" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleCommentSubmit} className="postcard-comment-form">
              <div className="postcard-comment-input-wrapper">
                <div
                  ref={commentTextareaRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleCommentInput}
                  onPaste={handleCommentPaste}
                  data-placeholder="Add a comment..."
                  className="postcard-comment-textarea"
                  role="textbox"
                  aria-multiline="true"
                />
                <button
                  type="submit"
                  disabled={!commentText.trim() || isSubmittingComment}
                  className="postcard-comment-submit-btn"
                  title={isSubmittingComment ? 'Posting...' : 'Post comment'}
                >
                  <ArrowUp size={18} />
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Custom Delete Confirmation Modal */}
      {showDeleteModal && createPortal(
        <div className="post-delete-modal-overlay">
          <div className="post-delete-modal-content">
            <div className="postcard-delete-modal-header">
              <h3>Delete Post</h3>
            </div>
            <div className="postcard-delete-modal-body">
              <p>If you delete this post, you won't be able to restore it.</p>
            </div>
            <div className="postcard-delete-modal-actions">
              <button
                onClick={cancelDelete}
                className="postcard-delete-modal-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="postcard-delete-modal-delete-button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Custom Edit Modal */}
      {showEditModal && createPortal(
        <div className="post-edit-modal-overlay">
          <div className="post-edit-modal-content">
            <div className="postcard-edit-modal-header">
              <h3>Edit Your Thoughts</h3>
              <button
                onClick={cancelEdit}
                className="postcard-edit-modal-close-button"
              >
                ×
              </button>
            </div>
            <div className="postcard-edit-modal-body">
              {/*<div className="edit-book-info">
                <strong>{post.content_title}</strong> by {post.content_author}
              </div>*/}
              <div
                ref={editTextareaRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditableInput}
                onPaste={(e) => {
                  e.preventDefault();

                  // Get HTML content from clipboard
                  const html = e.clipboardData.getData('text/html');
                  const text = e.clipboardData.getData('text/plain');

                  // Function to sanitize HTML using DOMPurify, keeping only bold and italic
                  const sanitizeHtml = (htmlString: string): string => {
                    return DOMPurify.sanitize(htmlString, {
                      ALLOWED_TAGS: ['strong', 'em', 'br'],
                      ALLOWED_ATTR: []
                    });
                  };

                  // Use HTML if available, otherwise convert plain text newlines to <br>
                  let contentToPaste;
                  if (html) {
                    // Convert <div> tags to <br> tags before sanitization
                    let convertedHtml = html
                      .replace(/<div[^>]*><br[^>]*><\/div>/g, '<br>') // Empty div with br -> single br
                      .replace(/<div[^>]*><\/div>/g, '<br>') // Empty div -> single br
                      .replace(/<div[^>]*>/g, '<br>') // Other divs -> br
                      .replace(/<\/div>/g, ''); // Remove closing divs
                    contentToPaste = sanitizeHtml(convertedHtml);
                  } else {
                    // Convert plain text newlines to <br> tags
                    contentToPaste = text.replace(/\n/g, '<br>');
                  }

                  // Insert at cursor position
                  const selection = window.getSelection();
                  if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    range.deleteContents();

                    // Insert as HTML
                    range.insertNode(document.createRange().createContextualFragment(contentToPaste));

                    // Move cursor to end of pasted content
                    range.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(range);

                    // Update state and check length
                    const currentDiv = e.currentTarget;
                    const currentText = currentDiv.innerText;
                    if (currentText.length > 1000) {
                      currentDiv.innerText = currentText.substring(0, 1000);
                    }
                    setEditContent(currentDiv.innerHTML);

                    // Auto-resize after paste
                    currentDiv.style.height = 'auto';
                    currentDiv.style.height = currentDiv.scrollHeight + 'px';
                  }
                }}
                data-placeholder="Share your thoughts about this book..."
                className="postcard-edit-textarea"
                role="textbox"
                aria-multiline="true"
              />

            </div>
            <div className="postcard-edit-modal-actions">
              {editContent.length > 1000 ? (
                <div className="postcard-edit-character-counter">
                  <span className="postcard-edit-character-count over-limit">
                    -{editContent.length - 1000}
                  </span>
                </div>
              ) : (
                <div></div>
              )}
              <button
                onClick={confirmEdit}
                disabled={isEditing || editContent.length > 1000}
                className="postcard-edit-modal-button postcard-edit-button-confirm"
              >
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Share Modal */}
      {showShareModal && createPortal(
        <div className="post-share-modal-overlay">
          <div className="post-share-modal-content">
            <div className="postcard-share-modal-header">
              <h3>Share</h3>
              <button
                onClick={handleShareClose}
                className="postcard-share-modal-close-button"
              >
                ×
              </button>
            </div>
            <div className="postcard-share-modal-body">
              <div className="postcard-share-preview">
                {post.content_type !== 'pdf' && (
                  <div className="postcard-share-cover">
                    {post.cover_image_url ? (
                      <img
                        src={post.cover_image_url}
                        loading="lazy"
                        alt={`Cover for ${post.content_title}`}
                        className={`postcard-share-cover-image ${post.content_type === 'podcast'
                          ? 'postcard-share-cover-image-podcast'
                          : post.content_type === 'article'
                            ? 'postcard-share-cover-image-article'
                            : 'postcard-share-cover-image-book'
                          }`}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const placeholder = target.nextElementSibling as HTMLElement;
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="postcard-share-pdf-placeholder"
                      style={{
                        display: post.cover_image_url ? 'none' : 'flex'
                      }}
                    >
                      {post.content_type === 'movie' ? (
                        <Film className="postcard-share-pdf-icon" />
                      ) : post.content_type === 'podcast' ? (
                        <Mic className="postcard-share-pdf-icon" />
                      ) : post.content_type === 'article' ? (
                        <LinkIcon className="postcard-share-pdf-icon" />
                      ) : (
                        <BookOpen className="postcard-share-pdf-icon" />
                      )}
                    </div>
                  </div>
                )}
                <div className="postcard-share-preview-content">
                  <h4 dangerouslySetInnerHTML={{ __html: sanitizeHtml(decodeHtmlEntities(post.content_title)) }}></h4>
                  {(post.content_type !== 'pdf' && !!post.content_author) && (
                    <p dangerouslySetInnerHTML={{ __html: `by ${sanitizeHtml(decodeHtmlEntities(post.content_author))}` }}></p>
                  )}
                </div>
              </div>

              <div className="postcard-share-options">
                <div className="postcard-social-share-section">
                  <div className="postcard-social-card">
                    <div
                      onClick={copyToClipboard}
                      className={`postcard-socialContainer postcard-containerFive ${copySuccess ? 'postcard-copy-success' : ''}`}
                      title={copySuccess ? 'Copied!' : 'Copy Link'}
                    >
                      <svg className="postcard-socialSvg copySvg" viewBox="0 0 24 24">
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                      </svg>
                    </div>
                    <div onClick={shareToFacebook} className="postcard-socialContainer postcard-containerOne">
                      <svg className="postcard-socialSvg facebookSvg" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </div>

                    <div onClick={shareToTwitter} className="postcard-socialContainer postcard-containerTwo">
                      <svg className="postcard-socialSvg twitterSvg" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </div>

                    <div onClick={shareToLinkedIn} className="postcard-socialContainer postcard-containerThree">
                      <svg className="postcard-socialSvg linkdinSvg" viewBox="0 0 448 512">
                        <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"></path>
                      </svg>
                    </div>

                    <div onClick={shareToWhatsApp} className="postcard-socialContainer postcard-containerFour">
                      <svg className="postcard-socialSvg whatsappSvg" viewBox="0 0 16 16">
                        <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Universal Toasts */}
      <UniversalToast type="copy" message="Link copied successfully!" show={showCopyToast} />
      <UniversalToast type="loading" message={loadingMessage} show={showLoadingToast} />
      <UniversalToast type="success" message={successMessage} show={showSuccessToast} />
      <UniversalToast type="error" message={errorMessage} show={showErrorToast} />
    </>
  );
};

export default PostCard;